/**
 * AniList Sync Service
 * Handles fetching and syncing anime data from AniList API
 * Implements Taiga-style incremental sync with rate limiting
 */

import { ANILIST_PROXY_URL } from '../config/constants';
import sqliteService from './sqliteService';

const SYNC_CONFIG = {
    BATCH_SIZE: 50, // Fetch 50 anime per request
    API_DELAY: 700, // 700ms delay between requests (Taiga's method)
    RETRY_ATTEMPTS: 5, // More retries for transient network errors (Failed to fetch)
    RETRY_DELAY: 60000, // 1 minute on rate limit
    RATE_LIMIT_PER_MINUTE: 90
};

class AniListSyncService {
    constructor() {
        this.isSyncing = false;
        this.syncProgress = { current: 0, total: 0, status: '' };
        this.requestCount = 0;
        this.requestResetTime = Date.now() + 60000;
    }

    /**
     * Make a rate-limited GraphQL request to AniList (via backend proxy)
     * @param {string} query GraphQL query
     * @param {Object} variables Query variables
     * @returns {Promise<Object>} Response data
     */
    async makeRateLimitedRequest(query, variables = {}, accessToken = null) {
        // Check rate limit
        if (Date.now() > this.requestResetTime) {
            this.requestCount = 0;
            this.requestResetTime = Date.now() + 60000;
        }

        if (this.requestCount >= SYNC_CONFIG.RATE_LIMIT_PER_MINUTE) {
            console.warn('⚠️ Rate limit reached, waiting...');
            await this.sleep(this.requestResetTime - Date.now());
            this.requestCount = 0;
            this.requestResetTime = Date.now() + 60000;
        }

        // Make request with retry logic
        let attempt = 1;
        while (true) {
            try {
                // Route through our backend proxy to avoid CORS.
                // Token is passed in the body; proxy forwards it as Authorization header.
                const response = await fetch(ANILIST_PROXY_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        query,
                        variables,
                        ...(accessToken ? { token: accessToken } : {})
                    })
                });

                this.requestCount++;

                if (response.status === 429) {
                    // Rate limited - RETRY INDEFINITELY
                    console.warn(`⚠️ Rate limited, attempt ${attempt} - waiting 60 seconds...`);
                    await this.sleep(SYNC_CONFIG.RETRY_DELAY);
                    attempt++;
                    continue; // Retry forever on rate limits
                }

                // Don't retry 401 errors - they indicate missing/invalid auth
                if (response.status === 401) {
                    const errorText = await response.text();
                    throw new Error(`Authentication required. Please connect OAuth in Settings.`);
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`❌ AniList API Error (${response.status}):`, errorText);
                    console.error('Query:', query.substring(0, 200) + '...');
                    console.error('Variables:', variables);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                const data = await response.json();

                if (data.errors) {
                    console.error('❌ GraphQL Errors:', data.errors);
                    console.error('Query:', query.substring(0, 200) + '...');
                    console.error('Variables:', variables);
                    throw new Error(data.errors[0].message);
                }

                // Add delay before next request (Taiga's method)
                await this.sleep(SYNC_CONFIG.API_DELAY);

                return data.data;
            } catch (error) {
                // For non-rate-limit errors, limit retries
                if (attempt >= SYNC_CONFIG.RETRY_ATTEMPTS) {
                    throw error;
                }
                // Network errors (Failed to fetch) get longer backoff since they're often transient
                const isNetworkError = error.message?.includes('Failed to fetch') || error.name === 'TypeError';
                const backoffMs = isNetworkError ? 3000 * attempt : 1000 * attempt;
                console.warn(`⚠️ ${isNetworkError ? 'Network error' : 'Error'}, retry ${attempt + 1}/${SYNC_CONFIG.RETRY_ATTEMPTS} in ${backoffMs / 1000}s...`);
                await this.sleep(backoffMs);
                attempt++;
            }
        }
    }

    /**
     * Sync user's personal anime list
     * This runs every 5 minutes
     * @param {string} username User's AniList username
     * @param {string} accessToken User's access token (optional for read-only)
     * @returns {Promise<Object>} Sync result
     */
    async syncUserList(username, accessToken = null, onUpdateCallback = null) {
        try {
            console.log(`🔄 Syncing user list for: ${username}...`);

            // Fetch the MediaListCollection for the user
            const query = `
                query ($userName: String) {
                    MediaListCollection(userName: $userName, type: ANIME) {
                        lists {
                            name
                            status
                            entries {
                                media {
                                    id
                                    idMal
                                    title {
                                        romaji
                                        english
                                        native
                                    }
                                    episodes
                                    duration
                                    status
                                    format
                                    description
                                    startDate {
                                        year
                                        month
                                        day
                                    }
                                    endDate {
                                        year
                                        month
                                        day
                                    }
                                    season
                                    seasonYear
                                    source
                                    countryOfOrigin
                                    isLicensed
                                    hashtag
                                    trailer {
                                        id
                                        site
                                    }
                                    averageScore
                                    meanScore
                                    popularity
                                    trending
                                    genres
                                    synonyms
                                    tags {
                                        id
                                        name
                                        description
                                        category
                                        rank
                                        isGeneralSpoiler
                                        isMediaSpoiler
                                        isAdult
                                    }
                                    studios {
                                        edges {
                                            node {
                                                id
                                                name
                                            }
                                            isMain
                                        }
                                    }
                                    coverImage {
                                        extraLarge
                                        large
                                        medium
                                        color
                                    }
                                    bannerImage
                                    isFavourite
                                    isAdult
                                    relations {
                                        edges {
                                            relationType
                                            node {
                                                id
                                            }
                                        }
                                    }
                                    nextAiringEpisode {
                                        id
                                        airingAt
                                        timeUntilAiring
                                        episode
                                    }
                                    airingSchedule(notYetAired: false) {
                                        nodes {
                                            episode
                                            airingAt
                                        }
                                    }
                                    updatedAt
                                }
                                id
                                mediaId
                                status
                                score
                                progress
                                progressVolumes
                                repeat
                                priority
                                private
                                notes
                                hiddenFromStatusLists
                                startedAt {
                                    year
                                    month
                                    day
                                }
                                completedAt {
                                    year
                                    month
                                    day
                                }
                                updatedAt
                                createdAt
                            }
                        }
                    }
                }
            `;

            const data = await this.makeRateLimitedRequest(query, { userName: username }, accessToken);

            if (!data || !data.MediaListCollection) {
                throw new Error('Invalid response from AniList');
            }
            // Update user anime data
            const lists = data.MediaListCollection.lists;
            let updatedCount = 0;

            // Process all entries
            const allUserData = [];

            lists.forEach(list => {
                list.entries.forEach(entry => {
                    const anime = entry.media;

                    // Collect user data for batch update
                    allUserData.push({
                        animeId: anime.id,
                        userData: {
                            status: entry.status,
                            progress: entry.progress,
                            score: entry.score,
                            notes: entry.notes,
                            startedAt: entry.startedAt,
                            completedAt: entry.completedAt
                        }
                    });

                    updatedCount++;
                });
            });

            // NOTE: We no longer save anime catalog or relations to IndexedDB.
            // The catalog lives on the server and is queried on-demand.

            // Delta-only write: only save entries that actually CHANGED
            // This reduces IndexedDB writes from ~2185/min to ~0-5/min,
            // dramatically reducing journal bloat.
            const existingEntries = await sqliteService.user_anime.toArray();
            const existingMap = new Map(existingEntries.map(e => [e.anime_id, e]));

            let writtenCount = 0;
            for (const { animeId, userData } of allUserData) {
                const existing = existingMap.get(animeId);
                // Only write if entry is new OR data actually changed
                if (!existing ||
                    existing.status !== (userData.status || null) ||
                    existing.progress !== (userData.progress || 0) ||
                    existing.score !== (userData.score || null) ||
                    existing.notes !== (userData.notes || null)) {
                    await sqliteService.updateUserAnime(animeId, userData);
                    writtenCount++;
                }
            }
            if (writtenCount > 0) {
                console.log(`💾 Saved ${writtenCount} changed user anime entries to database (${allUserData.length - writtenCount} unchanged, skipped)`);
            } else {
                console.log(`⏭️ No user anime entries changed — skipped all ${allUserData.length} writes`);
            }

            // Update sync status
            await sqliteService.updateSyncStatus('user_list', {
                lastSyncTime: Date.now(),
                animeCount: updatedCount,
                userName: username,
                status: 'success'
            });

            // Call callback with synced data for incremental updates
            if (onUpdateCallback) {
                onUpdateCallback({
                    lists: data.MediaListCollection.lists,
                    timestamp: Date.now(),
                    userName: username
                });
            }

            console.log(`✅ User list synced: ${updatedCount} entries`);

            return {
                success: true,
                updatedCount,
                message: `Synced ${updatedCount} anime`
            };

        } catch (error) {
            console.error('❌ User list sync failed:', error);

            sqliteService.updateSyncStatus('user_list', {
                lastSyncTime: Date.now(),
                status: 'failed',
                errorMessage: error.message
            });

            throw error;
        }
    }

    /**
     * Sync user's personal manga list
     * @param {string} username User's AniList username
     * @param {string} accessToken User's access token
     * @param {Function} onUpdateCallback Callback with synced data
     * @returns {Promise<Object>} Sync result
     */
    async syncUserMangaList(username, accessToken = null, onUpdateCallback = null) {
        try {
            console.log(`🔄 Syncing manga list for: ${username}...`);

            const query = `
                query ($userName: String) {
                    MediaListCollection(userName: $userName, type: MANGA) {
                        lists {
                            name
                            status
                            entries {
                                media {
                                    id
                                    idMal
                                    title {
                                        romaji
                                        english
                                        native
                                    }
                                    chapters
                                    volumes
                                    status
                                    format
                                    description
                                    startDate {
                                        year
                                        month
                                        day
                                    }
                                    endDate {
                                        year
                                        month
                                        day
                                    }
                                    source
                                    countryOfOrigin
                                    isLicensed
                                    averageScore
                                    meanScore
                                    popularity
                                    trending
                                    genres
                                    synonyms
                                    tags {
                                        id
                                        name
                                        description
                                        category
                                        rank
                                        isGeneralSpoiler
                                        isMediaSpoiler
                                        isAdult
                                    }
                                    coverImage {
                                        extraLarge
                                        large
                                        medium
                                        color
                                    }
                                    bannerImage
                                    isFavourite
                                    isAdult
                                    relations {
                                        edges {
                                            relationType
                                            node {
                                                id
                                                type
                                                format
                                                status
                                                chapters
                                                volumes
                                                averageScore
                                                title {
                                                    romaji
                                                    english
                                                    native
                                                }
                                                coverImage {
                                                    extraLarge
                                                    large
                                                    medium
                                                }
                                                startDate {
                                                    year
                                                    month
                                                    day
                                                }
                                                description
                                                genres
                                                bannerImage
                                            }
                                        }
                                    }
                                    updatedAt
                                }
                                id
                                mediaId
                                status
                                score
                                progress
                                progressVolumes
                                repeat
                                priority
                                private
                                notes
                                hiddenFromStatusLists
                                startedAt {
                                    year
                                    month
                                    day
                                }
                                completedAt {
                                    year
                                    month
                                    day
                                }
                                updatedAt
                                createdAt
                            }
                        }
                    }
                }
            `;

            const data = await this.makeRateLimitedRequest(query, { userName: username }, accessToken);

            if (!data || !data.MediaListCollection) {
                throw new Error('Invalid response from AniList (manga)');
            }

            const lists = data.MediaListCollection.lists;
            let updatedCount = 0;
            lists.forEach(list => {
                list.entries.forEach(() => updatedCount++);
            });

            // Call callback with synced data
            if (onUpdateCallback) {
                onUpdateCallback({
                    lists: data.MediaListCollection.lists,
                    timestamp: Date.now(),
                    userName: username
                });
            }

            console.log(`✅ Manga list synced: ${updatedCount} entries`);
            return {
                success: true,
                updatedCount,
                message: `Synced ${updatedCount} manga`
            };

        } catch (error) {
            console.error('❌ Manga list sync failed:', error);
            throw error;
        }
    }

    /**
     * Get sync progress (for UI)
     * @returns {Object} Progress information
     */
    getSyncProgress() {
        return {
            ...this.syncProgress,
            isSyncing: this.isSyncing
        };
    }

    /**
     * Sleep utility
     * @param {number} ms Milliseconds to sleep
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Export singleton instance
const anilistSyncService = new AniListSyncService();
export default anilistSyncService;
